import React, { CSSProperties, forwardRef, PropsWithChildren } from 'react'

import * as S from './styles'

type Props = {
  /**
   * The flex-direction CSS property sets how flex items are placed in the flex container defining the main axis and the direction (normal or reversed).
   *
   * @example
   * ```tsx
   * <FlexContainer direction="row" />
   * ```
   *
   * @default 'row'
   *
   */
  direction?: CSSProperties['flexDirection']

  /**
   * The align-items CSS property sets the align-self value on all direct children as a group. The align-self property sets the alignment of an item within its containing block.
   *
   * @example
   * ```tsx
   * <FlexContainer alignItems="center" />
   * ```
   *
   * @default 'center'
   *
   */
  alignItems?: CSSProperties['alignItems']

  /**
   * The justify-content CSS property defines how the browser distributes space between and around content items along the main-axis of a flex container, and the inline axis of a grid container.
   *
   * @example
   * ```tsx
   * <FlexContainer justifyContent="center" />
   * ```
   *
   * @default 'center'
   *
   */
  justifyContent?: CSSProperties['justifyContent']
  /**
   * The padding area is the space between its content and its border.
   *
   * @example
   * ```tsx
   * <FlexContainer padding="0 16px" />
   * ```
   *
   * @default 'center'
   *
   */
  padding?: CSSProperties['padding']
  /**
   * The style prop is used to pass styles to the component.
   *
   * @example
   * ```tsx
   * <FlexContainer style={{padding: "16px"}} />
   * ```
   *
   */
  style?: CSSProperties
}

export type FlexContainerProps = PropsWithChildren<Props>

export const FlexContainer = forwardRef<HTMLDivElement, FlexContainerProps>(
  ({ children, ...rest }, ref) => (
    <S.Flex ref={ref} {...rest}>
      {children}
    </S.Flex>
  ),
)
